// assets/homepage-market-intelligence.js

class MarketIntelligenceSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.layoutStyle = section.classList.contains('market-intelligence--carousel') ? 'carousel' : 
                      section.classList.contains('market-intelligence--timeline') ? 'timeline' : 
                      section.classList.contains('market-intelligence--dashboard') ? 'dashboard' : 'grid';
    
    this.init();
  }
  
  init() {
    // Initialize based on layout style
    switch(this.layoutStyle) {
      case 'carousel':
        this.initCarousel();
        break;
      case 'timeline':
        this.initTimeline();
        break;
      case 'dashboard':
        this.initDashboard();
        break;
      default:
        this.initGrid();
    }
    
    // Common initializations
    this.initAnimations();
    this.initSubscribeForm();
    this.initSentimentGauge();
  }
  
  // Initialize Carousel Layout
  initCarousel() {
    const carousel = this.section.querySelector('.articles-carousel');
    if (!carousel) return;
    
    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      // Load Swiper dynamically if not already loaded
      this.loadSwiper().then(() => {
        this.createSwiper(carousel);
      });
    } else {
      this.createSwiper(carousel);
    }
  }
  
  createSwiper(carousel) {
    new Swiper(carousel, {
      slidesPerView: 1,
      spaceBetween: 24,
      loop: true,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },
      breakpoints: {
        640: {
          slidesPerView: 2
        },
        1024: {
          slidesPerView: 3
        }
      }
    });
  }
  
  loadSwiper() {
    return new Promise((resolve) => {
      if (document.querySelector('script[src*="swiper"]')) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/swiper@10/swiper-bundle.min.js';
      script.onload = resolve;
      document.head.appendChild(script);
      
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/swiper@10/swiper-bundle.min.css';
      document.head.appendChild(link);
    });
  }
  
  // Initialize Grid Layout Animations
  initGrid() {
    const articles = this.section.querySelectorAll('.grid-article, .featured-article');
    
    // Intersection Observer for entrance animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('animate-in');
          }, index * 100);
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });
    
    articles.forEach(article => {
      article.classList.add('animate-ready');
      observer.observe(article);
    });
  }
  
  // Initialize Timeline Layout
  initTimeline() {
    const timelineItems = this.section.querySelectorAll('.timeline-item');
    
    timelineItems.forEach((item, index) => {
      item.style.setProperty('--timeline-delay', `${index * 150}ms`);
    });
    
    // Add scroll-triggered animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('timeline-visible');
        }
      });
    }, {
      threshold: 0.3
    });
    
    timelineItems.forEach(item => observer.observe(item));
  }
  
  // Initialize Dashboard Layout
  initDashboard() {
    // Add any dashboard-specific functionality
    this.initMetricAnimations();
  }
  
  // Initialize metric number animations
  initMetricAnimations() {
    const metrics = this.section.querySelectorAll('[data-metric-value]');
    
    metrics.forEach(metric => {
      const finalValue = parseFloat(metric.dataset.metricValue);
      const decimals = metric.dataset.metricDecimals || 0;
      
      this.animateValue(metric, 0, finalValue, 1500, decimals);
    });
  }
  
  // Animate numeric values
  animateValue(element, start, end, duration, decimals = 0) {
    const range = end - start;
    const startTime = performance.now();
    
    const update = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = start + (range * easeOutQuart);
      
      element.textContent = current.toFixed(decimals);
      
      if (progress < 1) {
        requestAnimationFrame(update);
      }
    };
    
    requestAnimationFrame(update);
  }
  
  // Initialize sentiment gauge animation
  initSentimentGauge() {
    const gauge = this.section.querySelector('.sentiment-gauge__fill');
    if (!gauge) return;
    
    // Trigger animation on visibility
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          gauge.style.width = gauge.style.width || '50%';
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.5
    });
    
    observer.observe(gauge);
  }
  
  // Initialize subscribe form
  initSubscribeForm() {
    const form = this.section.querySelector('.subscribe-form');
    if (!form) return;
    
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const button = form.querySelector('.subscribe-form__button');
      const originalText = button.textContent;
      
      // Show loading state
      button.disabled = true;
      button.textContent = 'Subscribing...';
      
      try {
        // Submit form
        const formData = new FormData(form);
        const response = await fetch(form.action, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          button.textContent = 'Subscribed!';
          button.style.background = '#10b981';
          form.querySelector('input[type="email"]').value = '';
          
          // Reset after delay
          setTimeout(() => {
            button.textContent = originalText;
            button.style.background = '';
            button.disabled = false;
          }, 3000);
        } else {
          throw new Error('Subscription failed');
        }
      } catch (error) {
        button.textContent = 'Try Again';
        button.style.background = '#ef4444';
        
        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = '';
          button.disabled = false;
        }, 3000);
      }
    });
  }
  
  // Initialize general animations
  initAnimations() {
    // Add hover effect classes
    const cards = this.section.querySelectorAll('.featured-article, .grid-article, .carousel-article');
    
    cards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        card.style.transitionDelay = '0s';
      });
      
      card.addEventListener('mouseleave', () => {
        card.style.transitionDelay = '';
      });
    });
    
    // Parallax effect for premium gradient
    if (this.section.classList.contains('color-scheme--premium_gradient')) {
      this.initParallaxBackground();
    }
  }
  
  // Initialize parallax background effect
  initParallaxBackground() {
    let ticking = false;
    
    const updateBackground = () => {
      const scrolled = window.pageYOffset;
      const rate = scrolled * -0.5;
      
      this.section.style.backgroundPosition = `center ${rate}px`;
      ticking = false;
    };
    
    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateBackground);
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', requestTick);
  }
}

// Initialize all Market Intelligence sections on the page
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.market-intelligence-section');
  
  sections.forEach(section => {
    new MarketIntelligenceSection(section);
  });
});

// Reinitialize on Shopify section reload (theme editor)
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('market-intelligence-section')) {
      new MarketIntelligenceSection(event.target);
    }
  });
}